/**
 * PDF Studio signature stamps — memory + IndexedDB blobs, localStorage meta, Firebase sync.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { PdfSignature, PdfSignatureMethod } from '@canvas/shared-types';
import { signaturePath, userSignaturesPath } from '@canvas/shared-types';
import { STORAGE_PATHS } from '@canvas/firebase-config/config';
import { getClientAuth, getClientDB, getClientStorage } from '@canvas/firebase-config/client';
import {
  idbDeleteSignatureBlob,
  idbGetSignatureBlob,
  idbPutSignatureBlob,
} from '@/lib/pdf/signatureIdb';

export const MAX_SIGNATURES = 20;
const LOCAL_KEY = 'craftara:pdf-signatures-meta';

/** Session memory cache */
const blobCache = new Map<string, Blob>();

function path(segments: string) {
  return segments.replace(/^\/+/, '');
}

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return new Date();
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read signature image'));
    reader.readAsDataURL(blob);
  });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob | null> {
  if (!dataUrl?.startsWith('data:')) return null;
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return blob.size > 0 ? blob : null;
  } catch {
    try {
      const [, b64] = dataUrl.split(',');
      if (!b64) return null;
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'image/png' });
      return blob.size > 0 ? blob : null;
    } catch {
      return null;
    }
  }
}

/** Meta only in localStorage (no huge data URLs) */
function readLocalMeta(userId: string): PdfSignature[] {
  try {
    const raw = localStorage.getItem(`${LOCAL_KEY}:${userId}`);
    if (!raw) {
      // Migrate from older key that stored dataUrls
      return migrateLegacyLocal(userId);
    }
    const parsed = JSON.parse(raw) as Array<Record<string, unknown>>;
    return parsed.map((row) => normalizeSignature(row, String(row.signatureId || '')));
  } catch {
    return [];
  }
}

function writeLocalMeta(userId: string, list: PdfSignature[]): void {
  try {
    const payload = list.map((s) => ({
      signatureId: s.signatureId,
      userId: s.userId,
      name: s.name,
      method: s.method,
      storagePath: s.storagePath,
      // Prefer Storage URL in meta; tiny preview dataUrl optional
      downloadUrl: s.downloadUrl?.startsWith('data:') ? '' : s.downloadUrl || '',
      createdAt: s.createdAt.toISOString(),
      updatedAt: s.updatedAt.toISOString(),
    }));
    localStorage.setItem(`${LOCAL_KEY}:${userId}`, JSON.stringify(payload));
  } catch (err) {
    console.warn('Signature meta localStorage write failed', err);
  }
}

/** One-time: pull blobs out of legacy localStorage records into IndexedDB */
function migrateLegacyLocal(userId: string): PdfSignature[] {
  try {
    const legacy =
      localStorage.getItem(`craftara:pdf-signatures:${userId}`) ||
      localStorage.getItem(`pixkit:pdf-signatures:${userId}`);
    if (!legacy) return [];
    const parsed = JSON.parse(legacy) as Array<Record<string, unknown>>;
    const list = parsed.map((row) => normalizeSignature(row, String(row.signatureId || '')));

    void (async () => {
      for (const s of list) {
        const src = s.dataUrl || (s.downloadUrl.startsWith('data:') ? s.downloadUrl : '');
        if (!src) continue;
        const blob = await dataUrlToBlob(src);
        if (!blob) continue;
        blobCache.set(s.signatureId, blob);
        await idbPutSignatureBlob(s.signatureId, blob);
      }
      writeLocalMeta(
        userId,
        list.map((s) => ({
          ...s,
          dataUrl: undefined,
          downloadUrl: s.downloadUrl.startsWith('data:') ? '' : s.downloadUrl,
        }))
      );
      localStorage.removeItem(`craftara:pdf-signatures:${userId}`);
      localStorage.removeItem(`pixkit:pdf-signatures:${userId}`);
    })();

    return list;
  } catch {
    return [];
  }
}

export function normalizeSignature(
  data: Record<string, unknown>,
  fallbackId: string
): PdfSignature {
  const downloadUrl = (data.downloadUrl as string) || '';
  const dataUrl =
    (data.dataUrl as string) ||
    (downloadUrl.startsWith('data:image/') ? downloadUrl : undefined);
  return {
    signatureId: (data.signatureId as string) || fallbackId,
    userId: (data.userId as string) || '',
    name: (data.name as string) || 'Signature',
    method: (data.method as PdfSignatureMethod) || 'draw',
    storagePath: (data.storagePath as string) || '',
    downloadUrl,
    dataUrl,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export function getSignatureBlob(signatureId: string): Blob | undefined {
  return blobCache.get(signatureId);
}

export function setSignatureBlob(signatureId: string, blob: Blob): void {
  blobCache.set(signatureId, blob);
  void idbPutSignatureBlob(signatureId, blob);
}

/** Resolve PNG bytes for placement: memory → IndexedDB → dataUrl → Storage/fetch */
export async function resolveSignatureBlob(
  signature: PdfSignature
): Promise<Blob> {
  const id = signature.signatureId;

  const mem = blobCache.get(id);
  if (mem && mem.size > 0) return mem;

  try {
    const fromIdb = await idbGetSignatureBlob(id);
    if (fromIdb && fromIdb.size > 0) {
      blobCache.set(id, fromIdb);
      return fromIdb;
    }
  } catch (err) {
    console.warn('IndexedDB signature read failed', err);
  }

  if (signature.dataUrl?.startsWith('data:image/')) {
    const fromData = await dataUrlToBlob(signature.dataUrl);
    if (fromData) {
      blobCache.set(id, fromData);
      void idbPutSignatureBlob(id, fromData);
      return fromData;
    }
  }

  if (signature.downloadUrl?.startsWith('data:image/')) {
    const fromDl = await dataUrlToBlob(signature.downloadUrl);
    if (fromDl) {
      blobCache.set(id, fromDl);
      void idbPutSignatureBlob(id, fromDl);
      return fromDl;
    }
  }

  // Refresh Storage download URL if we only have a path
  let remoteUrl = signature.downloadUrl;
  if ((!remoteUrl || remoteUrl.startsWith('data:')) && signature.storagePath) {
    try {
      remoteUrl = await getDownloadURL(ref(getClientStorage(), signature.storagePath));
    } catch (err) {
      console.warn('getDownloadURL failed', err);
    }
  }

  if (remoteUrl && !remoteUrl.startsWith('data:')) {
    try {
      const res = await fetch(remoteUrl);
      if (res.ok) {
        const remote = await res.blob();
        if (remote.size > 0) {
          blobCache.set(id, remote);
          void idbPutSignatureBlob(id, remote);
          return remote;
        }
      }
    } catch (err) {
      console.warn('Fetch signature URL failed', err);
    }
  }

  throw new Error(
    'Signature image is missing — delete it and create a new signature'
  );
}

async function syncSignatureRemote(meta: PdfSignature, pngBlob: Blob): Promise<void> {
  try {
    const storagePath = STORAGE_PATHS.pdfSignatures(meta.userId, meta.signatureId);
    const storage = getClientStorage();
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, pngBlob, {
      contentType: 'image/png',
      customMetadata: { originalName: meta.name.slice(0, 120) },
    });
    const downloadUrl = await getDownloadURL(storageRef);

    const db = getClientDB();
    const now = new Date();
    await setDoc(doc(db, path(signaturePath(meta.userId, meta.signatureId))), {
      signatureId: meta.signatureId,
      userId: meta.userId,
      name: meta.name,
      method: meta.method,
      storagePath,
      downloadUrl,
      createdAt: Timestamp.fromDate(meta.createdAt),
      updatedAt: Timestamp.fromDate(now),
    });

    const list = readLocalMeta(meta.userId).map((s) =>
      s.signatureId === meta.signatureId
        ? { ...s, storagePath, downloadUrl, updatedAt: now }
        : s
    );
    writeLocalMeta(meta.userId, list);
  } catch (err) {
    console.warn('Signature cloud sync skipped', err);
  }
}

export async function listSignatures(userId: string): Promise<PdfSignature[]> {
  const local = readLocalMeta(userId);
  const byId = new Map(local.map((s) => [s.signatureId, s]));

  // Warm caches from IndexedDB for every known id
  await Promise.all(
    local.map(async (s) => {
      if (blobCache.has(s.signatureId)) return;
      try {
        const blob = await idbGetSignatureBlob(s.signatureId);
        if (blob && blob.size > 0) blobCache.set(s.signatureId, blob);
      } catch {
        /* ignore */
      }
    })
  );

  try {
    const db = getClientDB();
    const snap = await getDocs(collection(db, path(userSignaturesPath(userId))));
    for (const d of snap.docs) {
      const remote = normalizeSignature(d.data(), d.id);
      const prev = byId.get(remote.signatureId);
      byId.set(remote.signatureId, {
        ...remote,
        storagePath: remote.storagePath || prev?.storagePath || '',
        downloadUrl: remote.downloadUrl || prev?.downloadUrl || '',
        dataUrl: prev?.dataUrl,
      });

      // Prefetch remote blob into IDB if we don't have one yet
      if (!blobCache.has(remote.signatureId) && remote.downloadUrl) {
        void resolveSignatureBlob(byId.get(remote.signatureId)!).catch(() => undefined);
      }
    }
  } catch (err) {
    console.warn('Signature list from Firestore skipped', err);
  }

  return [...byId.values()].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
  );
}

export async function createSignature(options: {
  pngBlob: Blob;
  name: string;
  method: PdfSignatureMethod;
}): Promise<PdfSignature> {
  const auth = getClientAuth();
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('Sign in to save signatures');

  if (!options.pngBlob || options.pngBlob.size < 32) {
    throw new Error('Signature image is empty — try again');
  }

  const existing = readLocalMeta(userId);
  let allCount = existing.length;
  try {
    allCount = (await listSignatures(userId)).length;
  } catch {
    /* ignore */
  }

  if (allCount >= MAX_SIGNATURES) {
    throw new Error(`Signature limit reached (${MAX_SIGNATURES}). Delete one to add another.`);
  }

  const signatureId = crypto.randomUUID();
  const now = new Date();
  const name = options.name.trim() || `Signature ${existing.length + 1}`;

  // Durable local bitmap
  blobCache.set(signatureId, options.pngBlob);
  await idbPutSignatureBlob(signatureId, options.pngBlob);

  // Small preview for rail (optional)
  let preview: string | undefined;
  try {
    if (options.pngBlob.size < 120_000) {
      preview = await blobToDataUrl(options.pngBlob);
    }
  } catch {
    preview = undefined;
  }

  const meta: PdfSignature = {
    signatureId,
    userId,
    name,
    method: options.method,
    storagePath: '',
    downloadUrl: '',
    dataUrl: preview,
    createdAt: now,
    updatedAt: now,
  };

  writeLocalMeta(userId, [
    meta,
    ...existing.filter((s) => s.signatureId !== signatureId),
  ]);
  void syncSignatureRemote(meta, options.pngBlob);

  return meta;
}

export async function deleteSignature(
  userId: string,
  signature: PdfSignature
): Promise<void> {
  blobCache.delete(signature.signatureId);
  void idbDeleteSignatureBlob(signature.signatureId);

  const next = readLocalMeta(userId).filter(
    (s) => s.signatureId !== signature.signatureId
  );
  writeLocalMeta(userId, next);

  try {
    if (signature.storagePath) {
      await deleteObject(ref(getClientStorage(), signature.storagePath));
    }
  } catch {
    /* ignore */
  }

  try {
    await deleteDoc(doc(getClientDB(), path(signaturePath(userId, signature.signatureId))));
  } catch (err) {
    console.warn('Signature remote delete skipped', err);
  }
}
